import Checkbox from 'components/partials/checkbox/checkbox';
import RadioInput from 'components/partials/radio-input/radio-input';
import Toggle from 'components/partials/toggle/toggle';

import CurrencyField from './currency-field';

import {
  AidModelCalculationRequestCalculationGiftTargetType,
  AidModelCalculationRequestGrantCap,
} from 'api/generated/schemas';

import {
  GIFT_TARGET_OPTIONS,
  GRANT_CAP_OPTIONS,
} from '../calculations-step.constants';

import type {
  CalculationGiftTargetType,
  CalculationGrantCap,
  CalculationSectionVisibility,
  CalculationSettingsDraft,
} from '../calculations-step.types';

interface SettingsOption<TValue> {
  value: TValue;
  label: string;
}

export interface CalculationSettingsPanelProps {
  settings: CalculationSettingsDraft;
  visibility: CalculationSectionVisibility;
  onGiftTargetChange: (value: CalculationGiftTargetType) => void;
  onIncludeRequiredFeesChange: (value: boolean) => void;
  onAdjustmentLoansChange: (value: number | null) => void;
  onAdjustmentWorkStudyChange: (value: number | null) => void;
  onCalculateNewNeedChange: (value: boolean) => void;
  onGrantCapChange: (value: CalculationGrantCap) => void;
  onGrantCapAmountChange: (value: number | null) => void;
  giftTargetOptions?: ReadonlyArray<SettingsOption<CalculationGiftTargetType>>;
  grantCapOptions?: ReadonlyArray<SettingsOption<CalculationGrantCap>>;
  disabledGiftTargets?: ReadonlySet<CalculationGiftTargetType>;
  grantCapAmountError?: boolean;
  disabled?: boolean;
}

const legendClass = 'mb-3 font-semibold text-gray-700';

const CalculationSettingsPanel = ({
  settings,
  visibility,
  onGiftTargetChange,
  onIncludeRequiredFeesChange,
  onAdjustmentLoansChange,
  onAdjustmentWorkStudyChange,
  onCalculateNewNeedChange,
  onGrantCapChange,
  onGrantCapAmountChange,
  giftTargetOptions = GIFT_TARGET_OPTIONS,
  grantCapOptions = GRANT_CAP_OPTIONS,
  disabledGiftTargets,
  grantCapAmountError,
  disabled = false,
}: CalculationSettingsPanelProps) => {
  const showIncludeRequiredFees =
    visibility.includeRequiredFees &&
    settings.calculationGiftTargetType ===
      AidModelCalculationRequestCalculationGiftTargetType.TUITION;

  const showGrantCapAmount =
    settings.grantCap !== AidModelCalculationRequestGrantCap.NO_GRANT_CAP;

  return (
    <div
      className="flex flex-col gap-8"
      data-testid="calculation-settings-panel"
    >
      {visibility.giftTarget && (
        <fieldset className="m-0 border-0 p-0">
          <legend className={legendClass}>Calculate gift target using</legend>
          <div className="flex flex-wrap">
            {giftTargetOptions.map((option) => (
              <RadioInput
                key={option.value}
                labelFor={`gift-target-${option.value}`}
                label={option.label}
                radioValue={option.value}
                value={settings.calculationGiftTargetType}
                onChange={() => onGiftTargetChange(option.value)}
                disabled={disabled || !!disabledGiftTargets?.has(option.value)}
              />
            ))}
          </div>
        </fieldset>
      )}

      {showIncludeRequiredFees && (
        <Toggle
          isToggleOn={settings.includeRequiredFees}
          onToggleChange={onIncludeRequiredFeesChange}
          toggleText="Include required fees in target calculation"
          size="sm"
          labelPosition="right"
          disabled={disabled}
          data-testid="include-required-fees-toggle"
        />
      )}

      {visibility.loansWorkStudy && (
        <fieldset
          className="m-0 border-0 p-0"
          data-testid="loans-work-study-section"
        >
          <legend className="font-semibold text-gray-700">
            Adjustments to target
          </legend>
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
            <div className="w-full sm:w-64">
              <CurrencyField
                label="Loans"
                labelFor="adjustment-loans"
                value={settings.adjustmentLoans}
                onChange={onAdjustmentLoansChange}
                disabled={disabled}
                data-testid="adjustment-loans"
                inputCustomClass="h-8"
              />
            </div>
            <div className="w-full sm:w-64">
              <CurrencyField
                label="Work Study"
                labelFor="adjustment-work-study"
                value={settings.adjustmentWorkStudy}
                onChange={onAdjustmentWorkStudyChange}
                disabled={disabled}
                data-testid="adjustment-work-study"
                inputCustomClass="h-8"
              />
            </div>
          </div>
        </fieldset>
      )}

      {visibility.calculateNewNeed && (
        <Checkbox
          labelFor="calculate-new-need"
          label="Use recalculated Need to determine Need Ranks"
          checked={settings.calculateNewNeed}
          onChange={() => onCalculateNewNeedChange(!settings.calculateNewNeed)}
          disabled={disabled}
          inputCustomClass="size-3.5"
          customStylesLabel="w-fit"
          labelTextClass="text-sm text-gray-700"
        />
      )}

      {visibility.grantCap && (
        <fieldset className="m-0 border-0 p-0">
          <legend className={legendClass}>Grant cap</legend>
          <div className="flex flex-wrap">
            {grantCapOptions.map((option) => (
              <RadioInput
                key={option.value}
                labelFor={`grant-cap-${option.value}`}
                label={option.label}
                radioValue={option.value}
                value={settings.grantCap}
                onChange={() => onGrantCapChange(option.value)}
                disabled={disabled}
              />
            ))}
          </div>

          {showGrantCapAmount && (
            <div className="mt-3 w-full sm:w-64">
              <CurrencyField
                label="Grant cap amount"
                labelFor="grant-cap-amount"
                value={settings.grantCapAmount}
                onChange={onGrantCapAmountChange}
                required
                disabled={disabled}
                errorsNames={
                  grantCapAmountError && settings.grantCapAmount == null
                    ? { invalid: true }
                    : undefined
                }
                data-testid="grant-cap-amount"
                inputCustomClass="h-8"
              />
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
};

export default CalculationSettingsPanel;
